import { useState } from "react";

import { Trash } from "lucide-react";
import { useDispatch } from "react-redux";

import {
  BulkDeleteCasesResult,
  useDeleteMultipleCases,
} from "@/hooks/mutations/use-cases";
import { setMessage } from "@/redux/notification";

import DeleteIcon from "@assets/images/file-with-trash.png";

import { ConfirmationModal } from "../../ConfirmationModal";
import { Button } from "../../ui/button";

type DeleteCasesConfirmationProps = {
  caseIds: string[];
  // MDRS-30: parent owns the selection state — fired after a delete attempt
  // (success or partial-success) so the list resets and doesn't re-target IDs
  // the backend already removed.
  onCleared?: () => void;
};

const buildToastMessage = (
  attempted: number,
  {
    deletedCount,
    alreadyGoneCount,
    failedCount,
    outcomes,
  }: BulkDeleteCasesResult
): { content: string; type: "success" | "warning" | "error" } => {
  if (failedCount === 0) {
    // Aggregate "deleted" + "already-gone" into the success count — from the
    // user's perspective they're indistinguishable: the case is gone.
    return {
      content: `${deletedCount + alreadyGoneCount} cases deleted successfully`,
      type: "success",
    };
  }

  const successCount = deletedCount + alreadyGoneCount;
  if (successCount === 0) {
    return {
      content: `Failed to delete ${attempted} cases. ${outcomes
        .filter((o) => o.outcome === "failed")
        .map((o) => (o.outcome === "failed" ? o.error : ""))
        .join("; ")}`,
      type: "error",
    };
  }

  return {
    content: `Deleted ${successCount} of ${attempted} cases; ${failedCount} failed.`,
    type: "warning",
  };
};

export const DeleteCasesConfirmation = ({
  caseIds,
  onCleared,
}: DeleteCasesConfirmationProps) => {
  const dispatch = useDispatch();
  const deleteMultipleCases = useDeleteMultipleCases();
  const [isOpen, setIsOpen] = useState(false);

  const handleDelete = () => {
    const attempted = caseIds.length;
    deleteMultipleCases.mutate(caseIds, {
      onSuccess: (result) => {
        dispatch(setMessage(buildToastMessage(attempted, result)));
        setIsOpen(false);
        // Always clear — the rows that succeeded are gone, and the rows that
        // failed shouldn't sit re-selectable in the list either (the user can
        // re-select them deliberately after the cache refreshes).
        onCleared?.();
      },
      onError: (error) => {
        // mutationFn doesn't throw on per-case failures any more — this fires
        // only if our own code blows up (e.g. unexpected exception).
        dispatch(
          setMessage({
            content: error.message || "Failed to delete cases",
            type: "error",
          })
        );
        onCleared?.();
      },
    });
  };

  return (
    <ConfirmationModal
      type="warning"
      icon={
        <img
          src={DeleteIcon}
          alt="Delete Icon"
          className="size-14 text-destructive"
        />
      }
      title="Delete Selected Cases?"
      description="This action will permanently delete all selected cases and cannot be undone. Please confirm to proceed."
      confirmButtonText="Yes, Delete"
      cancelButtonText="No, Cancel"
      onConfirm={handleDelete}
      isLoading={deleteMultipleCases.isPending}
      open={isOpen}
      onOpenChange={setIsOpen}
    >
      <Button
        variant="destructive-outline"
        size="sm"
        disabled={deleteMultipleCases.isPending}
        onClick={() => setIsOpen(true)}
      >
        <Trash className="size-4 text-destructive" /> Delete
      </Button>
    </ConfirmationModal>
  );
};
